import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin.js'
import { Component } from '../core/smpl-components'
import { ContentFilter } from '../modules/content-filter'

gsap.registerPlugin(ScrollToPlugin)

// -----------------------------------------------------------------------------
// Shop filter — tabs with radio-style options over the product grid.
//
// The filtering itself is the shared ContentFilter engine (modules/content-
// filter.js and friends, copied unchanged from other projects). This component
// only adds the tab behaviour on top of its two groups:
//   - `tab`    one top-level category (or "prizes"); alone it shows all of it
//   - `option` one subcategory / prize within that tab
// `allowMultiple: false` makes both groups single-select, and clicking an
// active button deselects it. Groups are ANDed, so tab + option narrows down.
//
// Counting is off: FilterCounter disables filters that don't overlap the other
// groups, which would grey out the other tabs as soon as an option is picked.
// Options are always a subset of their tab, so there is nothing to disable.
//
// Below `l` the filter sits behind a "Filters" toggle: the panel opens as a
// full-screen overlay (page scroll-locked) with the tabs as an accordion (the
// same showOptions(), the rows are interleaved with their tabs) and a
// "Toon N resultaten" button that closes it again. Filtering is live
// underneath; scrolling back to the grid waits until the overlay closes.
//
// Tokens come from boekenberg_shop_filter_tabs() (functions/woocommerce.php).
// Selector: `.js-shop-filter` (see setup/components.js).
// -----------------------------------------------------------------------------
export default class extends Component {
	mount() {
		this.elFilters = this.element.querySelector('.js-content-filters')
		this.elItems = this.element.querySelector('.js-shop-filter-items')

		if (!this.elFilters || !this.elItems) return

		this.elsOptions = [...this.elFilters.querySelectorAll('.shop-filter__options')]
		this.elPanel = this.elFilters.querySelector('.shop-filter__panel')
		this.elToggle = this.elFilters.querySelector('.js-shop-filter-toggle')
		this.elClose = this.elFilters.querySelector('.js-shop-filter-close')
		this.elResults = this.elFilters.querySelector('.js-shop-filter-results')
		this.elCount = this.elFilters.querySelector('.js-shop-filter-count')
		this.isOpen = false

		// undefined until the first change, so the state restored from the URL
		// hash on load keeps its option instead of counting as a tab switch.
		this.activeTab = undefined

		this.contentFilter = new ContentFilter({
			elFilterContainer: this.elFilters,
			elContentContainer: this.elItems,
			displayStyle: 'flex',
			enableCounting: false,
			filterLogicOptions: {
				allowMultiple: false,
				history: true,
				setQueryParams: true,
			},
			cbOnFilterChange: (state) => this.onFilterChange(state),
		})

		// Tabs and options with a category page are links (for crawlers, see
		// shop-filter.twig); the engine filters on the click, the page stays.
		this.onLinkClick = (ev) => {
			if (ev.target.closest('a[data-filter]')) ev.preventDefault()
		}
		this.elFilters.addEventListener('click', this.onLinkClick)

		this.onToggleClick = () => this.open()
		this.onCloseClick = () => this.close()
		this.onResultsClick = () => this.close(true, () => this.scrollToStart())
		this.onKeydown = (ev) => {
			if (ev.key === 'Escape' && this.isOpen) this.close()
		}
		this.elToggle?.addEventListener('click', this.onToggleClick)
		this.elClose?.addEventListener('click', this.onCloseClick)
		this.elResults?.addEventListener('click', this.onResultsClick)
		document.addEventListener('keydown', this.onKeydown)

		// The panel is mobile-only; crossing to desktop must not leave it open
		// (or mid-animation) underneath the desktop layout. 992px is `l` in
		// _tokens/_breakpoints.scss.
		this.desktopQuery = window.matchMedia('(min-width: 992px)')
		this.onBreakpointChange = (ev) => {
			if (ev.matches) this.close(false)
		}
		this.desktopQuery.addEventListener('change', this.onBreakpointChange)
	}

	onFilterChange(state) {
		const tab = state.find((g) => g.group === 'tab')?.active[0] ?? null
		const option = state.find((g) => g.group === 'option')

		const isUserChange = this.activeTab !== undefined
		const switched = isUserChange && tab !== this.activeTab
		this.showOptions(tab, isUserChange)
		this.activeTab = tab

		// Runs before the engine has fully mounted too (state from the URL hash);
		// the count rendered in Twig covers that first moment.
		const matched = this.contentFilter?.filterDomHandler.getMatchedItems().length
		if (this.elCount && matched !== undefined) this.elCount.textContent = matched

		// While the mobile panel is open the grid is behind it; the results button
		// scrolls once it closes.
		if (isUserChange && !this.isOpen) this.scrollToStart()

		// The option belonged to the previous tab. Clearing it triggers one more
		// change, with the tab unchanged, so this doesn't recurse further.
		if (switched && option?.active.length) {
			this.contentFilter.filterInterface.filterLogic.deactivateGroup('option')
		}
	}

	// Opens, closes or swaps the option row. The row's height (with its top
	// padding, so the grid below moves smoothly) and the options themselves
	// move one after the other: the row grows first, then the options fade up
	// in a stagger; on close the options fade out before the row folds up.
	// Going from one tab's options to another's eases between the two heights.
	// The row only clips (overflow: hidden) while its height moves, so the
	// options are never cut off by its edge.
	showOptions(tab, animate) {
		// Tracked rather than read from `hidden`: a row that is folding up is
		// still visible, but already closed as far as the state goes.
		const current = this.openOptions
		const next = this.elsOptions.find((el) => el.dataset.tab === tab)

		if (current === next) return
		this.openOptions = next

		// Measured before anything is reset, so a swap mid-animation starts from
		// the height that is on screen.
		const fromHeight = current && !current.hidden ? current.offsetHeight : 0

		this.timeline?.kill()
		const options = this.elsOptions.flatMap((el) => [...el.children])
		gsap.set([...this.elsOptions, ...options], { clearProps: 'height,paddingTop,paddingBottom,overflow,opacity,visibility,transform' })

		this.elsOptions.forEach((el) => {
			el.hidden = el !== next
		})

		if (!animate) return

		const closed = { height: 0, paddingTop: 0, paddingBottom: 0 }
		this.timeline = gsap.timeline()

		if (next) {
			this.timeline
				.set(next.children, { autoAlpha: 0 })
				.fromTo(
					next,
					{ ...(fromHeight ? { height: fromHeight } : closed), overflow: 'hidden' },
					{
						height: next.offsetHeight,
						paddingTop: getComputedStyle(next).paddingTop,
						paddingBottom: getComputedStyle(next).paddingBottom,
						duration: 0.2,
						ease: 'power3.inOut',
						clearProps: 'height,paddingTop,paddingBottom,overflow',
					},
				)
				.to(next.children, {
					autoAlpha: 1,
					y: 0,
					startAt: { y: 4 },
					duration: 0.2,
					stagger: 0.01,
					ease: 'power2.out',
					clearProps: 'opacity,visibility,transform',
				})
		} else {
			current.hidden = false
			this.timeline
				.to(current.children, { autoAlpha: 0, duration: 0.15, ease: 'power1.out' })
				.set(current, { overflow: 'hidden' })
				.to(current, { ...closed, duration: 0.3, ease: 'power3.inOut' })
				.add(() => {
					current.hidden = true
					gsap.set([current, ...current.children], { clearProps: 'height,paddingTop,paddingBottom,overflow,opacity,visibility' })
				})
		}
	}

	// Mobile overlay. The toggle bar stays in the flow underneath, so the page
	// keeps its layout; the overlay fades in over it and the page is locked
	// through the shared Browser helper (core/browser.js), like the main nav.
	open() {
		if (this.isOpen) return

		// Read before the scroll lock, which may shift the page; close() reuses it.
		const offset = (this.openOffset = this.overlayOffset())

		this.isOpen = true
		this.elToggle?.setAttribute('aria-expanded', 'true')
		this.elFilters.classList.add('is-open')
		window.app.browser.disableScroll(this.elPanel)

		this.panelTween?.kill()
		this.panelTween = gsap.fromTo(
			this.elPanel,
			{ autoAlpha: 0, y: offset },
			{ autoAlpha: 1, y: 0, duration: 0.3, ease: 'power2.out', clearProps: 'opacity,visibility,transform' },
		)
	}

	// `onClosed` runs once the page scrolls again (the results button scrolls
	// to the grid from there; while locked the scroll would be undone).
	close(animate = true, onClosed) {
		if (!this.isOpen) return

		this.isOpen = false
		this.elToggle?.setAttribute('aria-expanded', 'false')
		this.panelTween?.kill()

		const done = () => {
			this.elFilters.classList.remove('is-open')
			gsap.set(this.elPanel, { clearProps: 'opacity,visibility,transform' })
			window.app.browser.enableScroll()
			onClosed?.()
		}

		if (!animate) return done()

		this.panelTween = gsap.to(this.elPanel, {
			autoAlpha: 0,
			y: this.openOffset,
			duration: 0.25,
			ease: 'power2.in',
			onComplete: done,
		})
	}

	// How far the overlay rises as it fades in (and sinks as it fades out).
	// Stuck at the top, the overlay's header lands exactly on the bar, so any
	// movement would make the bar jump: fade only.
	overlayOffset() {
		const bar = this.elFilters.closest('.shop-layout__filter') ?? this.elFilters
		return bar.getBoundingClientRect().top <= 0 ? 0 : 16
	}

	// With the filter stuck at the top, a change would only show further down
	// the grid. Scroll back to where the filter sits in the flow, so the grid
	// starts right under it. Nothing to do when the page isn't scrolled past it.
	scrollToStart() {
		const top = this.element.getBoundingClientRect().top + window.scrollY

		if (window.scrollY <= top) return

		gsap.to(window, { scrollTo: top, duration: 0.5, ease: 'power2.inOut', overwrite: true })
	}

	unmount() {
		gsap.killTweensOf(window)
		this.timeline?.kill()
		if (this.isOpen) this.close(false)
		this.panelTween?.kill()
		if (this.contentFilter) this.contentFilter.destroy()

		this.elFilters?.removeEventListener('click', this.onLinkClick)
		document.removeEventListener('keydown', this.onKeydown)
		this.elClose?.removeEventListener('click', this.onCloseClick)
		this.elToggle?.removeEventListener('click', this.onToggleClick)
		this.elResults?.removeEventListener('click', this.onResultsClick)
		this.desktopQuery?.removeEventListener('change', this.onBreakpointChange)
	}
}
